import type { ErrorNote, Job } from "../types";
import { getJobAgeColor } from "./jobAgeColor";
import {
	jobListCompanyOf,
	jobListErrorTypesOf,
	jobListGrowerOf,
	jobListStatusOf,
	tallyJobs,
	type JobListFacet,
} from "./organizeJobList";

/** One field across the selected jobs: the value they all share, or each value with how many jobs have it. */
export type SelectionField = { kind: "shared"; value: string } | { kind: "mixed"; values: JobListFacet[] };

/** What a set of selected jobs have in common, from the fields a job list already carries. */
export interface JobSelectionSummary {
	total: number;
	/** Each error type with how many of the jobs have it, most common first. */
	errorTypes: JobListFacet[];
	company: SelectionField;
	grower: SelectionField;
	status: SelectionField;
	shift: SelectionField;
	deal: SelectionField;
	branch: SelectionField;
	season: SelectionField;
	testPackage: SelectionField;
	/** Each job flag with how many of the jobs carry it. */
	flags: JobListFacet[];
	testJobs: number;
	/** The age span in business days (what the job cards show), and the jobs with no date to count from. */
	age: { oldestDays: number | null; newestDays: number | null; undated: number };
}

export interface JobSelectionSummaryOptions {
	/** The errors the list shows for a job. */
	errorsOf: (job: Job) => readonly ErrorNote[];
	/** The job date its age counts from. */
	dateOf: (job: Job) => Date | string | null | undefined;
	/** Display names; a missing name falls back to the raw id. */
	shiftNames?: ReadonlyMap<string, string>;
	dealNames?: ReadonlyMap<string, string>;
	branchNames?: ReadonlyMap<string, string>;
	now: Date;
}

const named = (id: string | undefined, names: ReadonlyMap<string, string> | undefined, none: string) =>
	id ? names?.get(id) || id : none;

const hasDate = (date: Date | string | null | undefined) =>
	!!date && !Number.isNaN((date instanceof Date ? date : new Date(date)).getTime());

/** What the selected jobs share, field by field; error types first. */
export function summarizeJobSelection(jobs: readonly Job[], options: JobSelectionSummaryOptions): JobSelectionSummary {
	const field = (valueOf: (job: Job) => string): SelectionField => {
		const values = tallyJobs(jobs, (job) => [valueOf(job)]);
		return values.length === 1 ? { kind: "shared", value: values[0].value } : { kind: "mixed", values };
	};

	const ages = jobs
		.map((job) => options.dateOf(job))
		.filter(hasDate)
		.map((date) => getJobAgeColor(date, options.now).businessDaysOld);

	return {
		total: jobs.length,
		errorTypes: tallyJobs(jobs, (job) => jobListErrorTypesOf(options.errorsOf(job))),
		company: field(jobListCompanyOf),
		grower: field(jobListGrowerOf),
		status: field(jobListStatusOf),
		shift: field((job) => named(job.shiftId, options.shiftNames, "No Shift")),
		deal: field((job) => named(job.dealIds?.[0], options.dealNames, "No Deal")),
		branch: field((job) => named(job.branchIds?.[0], options.branchNames, "No Branch")),
		season: field((job) => job.season || "No Season"),
		testPackage: field((job) => job.testPackage || "No Test Package"),
		flags: tallyJobs(jobs, (job) => job.jobFlags ?? []),
		testJobs: jobs.filter((job) => job.isTestJob).length,
		age: {
			oldestDays: ages.length > 0 ? Math.max(...ages) : null,
			newestDays: ages.length > 0 ? Math.min(...ages) : null,
			undated: jobs.length - ages.length,
		},
	};
}
